import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

// FIX: Removed the local `declare global` block for `window.aistudio`. A global type `AIStudio`
// already exists, and redeclaring it was causing a TypeScript conflict.
// The existing global type is assumed to be available in the build environment.

interface ApiKeyContextType {
  isKeySelected: boolean;
  isChecking: boolean;
  selectKey: () => Promise<void>;
}

const ApiKeyContext = createContext<ApiKeyContextType | undefined>(undefined);

export const ApiKeyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isKeySelected, setIsKeySelected] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  const checkKey = useCallback(async () => {
    if (window.aistudio) {
      // Platform environment: Use the official API to check.
      try {
        const hasKey = await window.aistudio.hasSelectedApiKey();
        setIsKeySelected(hasKey);
      } catch (e) {
        console.error('Error checking for platform API key:', e);
        setIsKeySelected(false);
      }
    } else {
      // Local development environment: Check localStorage as a fallback.
      const localKey = localStorage.getItem('GEMINI_API_KEY');
      setIsKeySelected(!!localKey);
    }
    setIsChecking(false);
  }, []);
  
  const resetKeyState = useCallback(() => {
    console.warn('API key error detected. Prompting user to re-select key.');
    // For local dev, also clear the potentially bad key from storage
    if (!window.aistudio) {
        localStorage.removeItem('GEMINI_API_KEY');
    }
    setIsKeySelected(false);
  }, []);

  useEffect(() => {
    checkKey();
    
    // Listen for storage changes to react if the user sets the key in another tab.
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'GEMINI_API_KEY') {
        checkKey();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('apiKeyError', resetKeyState);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('apiKeyError', resetKeyState);
    };
  }, [checkKey, resetKeyState]);

  const selectKey = async () => {
    if (window.aistudio) {
      try {
        await window.aistudio.openSelectKey();
        // Assume key selection is successful to avoid race conditions.
        setIsKeySelected(true);
      } catch (e) {
        console.error('Error opening API key selection:', e);
      }
    } else {
      // For local development, use a browser prompt to get the key.
      const key = prompt("You are in a local development environment.\n\nPlease enter your Gemini API key to proceed. The key will be stored in your browser's local storage.");
      if (key && key.trim()) {
        localStorage.setItem('GEMINI_API_KEY', key.trim());
        setIsKeySelected(true);
      }
    }
  };

  const value = { isKeySelected, isChecking, selectKey };

  return (
    <ApiKeyContext.Provider value={value}>
      {children}
    </ApiKeyContext.Provider>
  );
};

export const useApiKey = (): ApiKeyContextType => {
  const context = useContext(ApiKeyContext);
  if (context === undefined) {
    throw new Error('useApiKey must be used within an ApiKeyProvider');
  }
  return context;
};